import { useEffect, useRef, useState } from "react";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";

interface AddressAutocompleteProps {
  value: string;
  onChange: (value: string) => void;
  onPlaceSelected: (place: { address: string; lat: number; lng: number }) => void;
  disabled?: boolean;
  isLoaded: boolean; // kept for compatibility but handled server-side now
}

interface Suggestion {
  description: string;
  address: string;
  lat: number;
  lng: number;
}

export const AddressAutocomplete = ({
  value,
  onChange,
  onPlaceSelected,
  disabled,
}: AddressAutocompleteProps) => {
  const [suggestions, setSuggestions] = useState<Suggestion[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const abortRef = useRef<AbortController | null>(null);
  const justSelectedRef = useRef(false);

  useEffect(() => {
    if (!value || value.trim().length < 3) {
      setSuggestions([]);
      if (abortRef.current) abortRef.current.abort();
      return;
    }

    // Skip autocomplete if we just selected an address
    if (justSelectedRef.current) {
      return;
    }

    const handler = setTimeout(async () => {
      try {
        if (abortRef.current) abortRef.current.abort();
        const controller = new AbortController();
        abortRef.current = controller;

        setIsLoading(true);

        const baseUrl = import.meta.env.VITE_SUPABASE_URL;
        const apiKey = import.meta.env.VITE_SUPABASE_PUBLISHABLE_KEY;

        const res = await fetch(`${baseUrl}/functions/v1/address-autocomplete`, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            apikey: apiKey,
          },
          body: JSON.stringify({ input: value.trim() }),
          signal: controller.signal,
        });

        if (!res.ok) {
          console.error("Autocomplete edge function failed", res.status);
          setSuggestions([]);
          return;
        }

        const data = await res.json();
        setSuggestions(data.suggestions ?? []);
      } catch (err) {
        if ((err as any)?.name === "AbortError") return;
        console.error("Autocomplete request error", err);
      } finally {
        setIsLoading(false);
      }
    }, 300);

    return () => {
      clearTimeout(handler);
    };
  }, [value]);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = e.target.value;
    onChange(newValue);
  };

  const handleSelect = (suggestion: Suggestion) => {
    justSelectedRef.current = true;
    onChange(suggestion.address);
    setSuggestions([]);
    
    try {
      onPlaceSelected({
        address: suggestion.address,
        lat: suggestion.lat,
        lng: suggestion.lng,
      });
    } catch (err) {
      console.error("onPlaceSelected handler failed", err);
      toast.error("Failed to use selected address. Please try again.");
    }
    
    // Reset the flag after a delay
    setTimeout(() => {
      justSelectedRef.current = false;
    }, 500);
  };

  return (
    <div className="relative w-full">
      <Input
        placeholder="123 Main St, City, State ZIP"
        value={value}
        onChange={handleInputChange}
        disabled={disabled}
        className="w-full"
      />
      {isLoading && (
        <div className="absolute right-3 top-1/2 -translate-y-1/2 text-xs text-muted-foreground">
          Searching...
        </div>
      )}
      {suggestions.length > 0 && (
        <div className="absolute z-50 mt-1 w-full rounded-md border bg-background shadow-md max-h-60 overflow-auto text-sm">
          {suggestions.map((s) => (
            <button
              key={`${s.address}-${s.lat}-${s.lng}`}
              type="button"
              className="block w-full px-3 py-2 text-left hover:bg-muted hover:text-foreground"
              onClick={() => handleSelect(s)}
            >
              <div className="font-medium truncate">{s.address}</div>
              {s.description !== s.address && (
                <div className="text-xs text-muted-foreground truncate">{s.description}</div>
              )}
            </button>
          ))}
        </div>
      )}
    </div>
  );
};
